import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Core } from "@pdftron/webviewer";
import { Text } from "styles/utility-components";

import { createUseDebounce } from "lib/debounce";
import { useDocumentHighlight } from "lib/hooks/api/documents/useDocumentHighlight";
import { useFileViewerInstance } from "../../lib/core_components/FileViewer";
import { useURLState } from "../../lib/hooks/useURLState";
import DocumentSearchResults, { SearchResultSnippet } from "./DocumentSearchResults";
import FilterForm, { DocumentViewerFilters, DocumentViewerFiltersSchema } from "./FilterForm";

type DocumentSearchProps = {
  documentId?: string;
  defaultFilters: DocumentViewerFilters;
};

export default function DocumentSearch({ documentId, defaultFilters }: DocumentSearchProps) {
  const { instance } = useFileViewerInstance();
  const [documentReady, setIsDocumentReady] = useState(false);

  const [isSearchInProgress, setIsSearchInProgress] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResultSnippet[] | null>(null);
  const [activeResultIndex, setActiveResultIndex] = useState<number>(0);

  const [filters, setFilters] = useURLState(
    "docViewerFilters",
    defaultFilters,
    DocumentViewerFiltersSchema,
  );

  const { data: matches, isFetching: isHighlightsLoading } = useDocumentHighlight(
    documentId ?? "",
    { keywords: filters.keywords },
    {
      enabled: !!documentId && !!filters.keywords.length,
    },
  );

  const useDebounce = createUseDebounce(500);
  const debouncedSetFilters = useDebounce((filters: DocumentViewerFilters) => {
    setFilters(filters);
  });

  const filterCount = filters.keywords.length;

  useEffect(() => {
    if (!instance) return;

    setIsError(false);
    setIsSearchInProgress(true);

    const { documentViewer, Search } = instance.Core;

    const termsToHighlight = matches?.contentHighlights;

    if (!termsToHighlight?.length && !isHighlightsLoading) {
      // clear search results if no matches found
      setSearchResults([]);
      documentViewer.clearSearchResults();
      setIsSearchInProgress(false);
    }

    if (documentReady) {
      const searchText = termsToHighlight?.length ? termsToHighlight.join("|") : "";

      let index = 0;
      const results: SearchResultSnippet[] = [];

      if (searchText) {
        documentViewer.textSearchInit(
          searchText,
          Search.Mode.PAGE_STOP |
            Search.Mode.HIGHLIGHT |
            Search.Mode.AMBIENT_STRING |
            Search.Mode.REGEX |
            Search.Mode.WHOLE_WORD,
          {
            fullSearch: true,
            onError: () => {
              setIsSearchInProgress(false);
              setIsError(true);
            },
            onResult: (result: Core.Search.SearchResult) => {
              if (result.resultCode === Search.ResultCode.FOUND) {
                results.push({ ...result, originalIndex: index });
                index++;
              }
            },
            onDocumentEnd: () => {
              setIsSearchInProgress(false);
              if (results.length) {
                documentViewer.displayAdditionalSearchResults(results);
                documentViewer.setActiveSearchResult(results[0]);
                setActiveResultIndex(0);
                setSearchResults(results);
              }
            },
          },
        );
      }
    }
  }, [documentReady, matches, instance, isHighlightsLoading, filterCount]);

  const prevDocumentIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const documentViewer = instance?.Core.documentViewer;

    const handleIsDocumentReady = () => setIsDocumentReady(true);

    // Detect navigation: a previous documentId existed and it differs from the current one.
    // On fresh mount (undefined → id) or same-id re-runs (StrictMode), isNavigation is false.
    const isNavigation =
      prevDocumentIdRef.current !== undefined && prevDocumentIdRef.current !== documentId;
    prevDocumentIdRef.current = documentId;

    if (documentViewer) {
      // getDocument() becomes truthy at documentLoaded, which fires before annotationsLoaded.
      // Checking it here means there's a narrow race if this component mounts in the window
      // between those two events — search may run before annotations are fully ready.
      // getAnnotationsList() is not a reliable alternative because it returns [] for docs
      // with no annotations, even after annotationsLoaded has fired.
      if (documentViewer.getDocument() && !isNavigation) {
        setIsDocumentReady(true);
      }
      documentViewer.addEventListener("annotationsLoaded", handleIsDocumentReady);
    }

    return () => {
      if (documentViewer) {
        documentViewer.removeEventListener("annotationsLoaded", handleIsDocumentReady);
        setIsDocumentReady(false);
      }
    };
  }, [instance, documentId]);

  const handleJumpToSearchResult = (result: SearchResultSnippet) => {
    if (instance) {
      instance.Core.documentViewer.setActiveSearchResult(result);
    }
  };

  return (
    <>
      <FiltersContainer>
        <Text>Search for keywords</Text>
        <FilterForm filters={filters} onChange={debouncedSetFilters} />
      </FiltersContainer>
      <DocumentSearchResults
        activeResultIndex={activeResultIndex}
        setActiveResultIndex={setActiveResultIndex}
        searchResults={searchResults}
        isLoading={isSearchInProgress}
        onJumpToSearchResult={handleJumpToSearchResult}
        filterCount={filters.keywords.length}
        isError={isError}
      />
    </>
  );
}

const FiltersContainer = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "24px 24px 0",
  width: "100%",
  overflowY: "auto",
  "&::after": {
    content: '""',
    height: 24,
    width: "95%",
    alignSelf: "center",
    borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  },
}));
